import { useEffect, useState } from 'react'
import { formatMoney } from '../lib/format.js'

// Modal for adding or editing a single staff member.
// Mirrors SupplierEditModal pattern. Used from Settings → Staff list.
//
// Required field: name. Everything else is optional but role + wage
// are strongly encouraged because they drive labor-cost calculations
// on the Huddle.
export default function StaffEditModal({
  open,
  onClose,
  onSave,        // (id, patch) → Promise   (edit mode)
  onCreate,      // (payload) → Promise<row> (create mode)
  staff,         // optional; null = create mode
}) {
  const isCreateMode = !staff
  const [form, setForm] = useState(emptyForm())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open) return
    if (staff) {
      setForm({
        name:         staff.name ?? '',
        role:         staff.role ?? '',
        hourly_wage:  staff.hourly_wage != null ? String(staff.hourly_wage) : '',
        email:        staff.email ?? '',
        phone:        staff.phone ?? '',
        notes:        staff.notes ?? '',
        active:       !!staff.active,
      })
    } else {
      setForm(emptyForm())
    }
    setError(null)
  }, [open, staff])

  if (!open) return null

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e) {
    e?.preventDefault?.()
    if (submitting) return
    if (!form.name.trim()) {
      setError('Name is required.')
      return
    }
    if (form.hourly_wage && Number(form.hourly_wage) < 0) {
      setError('Hourly wage must be ≥ 0.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      if (isCreateMode) {
        await onCreate({
          name: form.name,
          role: form.role,
          hourly_wage: form.hourly_wage,
          email: form.email,
          phone: form.phone,
          notes: form.notes,
        })
      } else {
        await onSave(staff.id, {
          name: form.name.trim(),
          role: form.role.trim() || null,
          hourly_wage: form.hourly_wage ? Number(form.hourly_wage) : null,
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          notes: form.notes.trim() || null,
          active: form.active,
        })
      }
      onClose?.()
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('[StaffEditModal] save failed', e2)
      setError('Could not save. Check your connection and try again.')
    } finally {
      // Always reset — without this, `submitting` stays true after a
      // successful save, and the NEXT time the modal opens the submit
      // button is disabled and the early-return guard blocks all saves
      // until a full page reload. This caused the "stuck at 2 staff" bug.
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <form
        onSubmit={handleSubmit}
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={isCreateMode ? 'Add staff' : `Edit ${staff.name}`}
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium truncate">
              {isCreateMode ? 'Add staff member' : `Edit · ${staff.name}`}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="staff-name">Name *</label>
            <input
              id="staff-name"
              type="text"
              required
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Jamie Lopez"
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="staff-role">Role</label>
              <input
                id="staff-role"
                type="text"
                value={form.role}
                onChange={(e) => set('role', e.target.value)}
                placeholder="pitmaster / line cook / server"
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="staff-wage">
                Hourly wage
              </label>
              <input
                id="staff-wage"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.25"
                value={form.hourly_wage}
                onChange={(e) => set('hourly_wage', e.target.value)}
                placeholder="18.00"
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="staff-email">Email</label>
              <input
                id="staff-email"
                type="email"
                inputMode="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="jamie@example.com"
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="staff-phone">Phone</label>
              <input
                id="staff-phone"
                type="tel"
                inputMode="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" htmlFor="staff-notes">
              Notes (optional)
            </label>
            <textarea
              id="staff-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Availability constraints, certifications, etc."
              className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          {!isCreateMode && (
            <div className="flex items-center justify-between rounded-lg border border-black/10 px-3 py-3">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-black/55">
                  Inactive staff are hidden from roster picker and labor calculations.
                </div>
              </div>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => set('active', e.target.checked)}
                className="h-6 w-6 rounded border-black/30 text-blue-600 focus:ring-blue-600"
              />
            </div>
          )}
        </div>

        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="submit"
            disabled={submitting}
            className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? 'Saving…'
              : isCreateMode
                ? 'Add staff member'
                : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  )
}

function emptyForm() {
  return {
    name: '',
    role: '',
    hourly_wage: '',
    email: '',
    phone: '',
    notes: '',
    active: true,
  }
}
